/* RESPONSIBLE TEAM: team-standalone */

import { tracked } from '@glimmer/tracking';
import { type FieldType, type IntercomAttributeType } from './common';
import { TicketField } from 'embercom/objects/standalone/data-fields/zendesk-ticket-field';
import { UserField } from 'embercom/objects/standalone/data-fields/zendesk-user-field';
import { OrganizationField } from 'embercom/objects/standalone/data-fields/zendesk-organization-field';

const BUILT_IN_ATTRIBUTES = {
  zendeskUserFields: [
    {
      platformIdentifier: 'id',
      intercomAttribute: 'user_id',
      intercomFieldType: 'string' as IntercomAttributeType,
    },
    {
      platformIdentifier: 'name',
      intercomAttribute: 'name',
      intercomFieldType: 'string' as IntercomAttributeType,
    },
    {
      platformIdentifier: 'email',
      intercomAttribute: 'email',
      intercomFieldType: 'string' as IntercomAttributeType,
    },
  ],
  ticketFields: ['id', 'subject', 'description'],
  organizationFields: [
    {
      platformIdentifier: 'id',
      intercomAttribute: 'remote_company_id',
      intercomFieldType: 'string' as IntercomAttributeType,
    },
    {
      platformIdentifier: 'name',
      intercomAttribute: 'name',
      intercomFieldType: 'string' as IntercomAttributeType,
    },
  ],
};

type ZendeskAdmin = {
  id: number;
  name: string;
  email: string;
  photo: string;
};

export type ZendeskTicketsSetupData = {
  admins: Array<ZendeskAdmin>;
  email_addresses: Array<string>;
  live_trigger: { id?: number };
};

export class ZendeskDataFields {
  @tracked userFields: UserField[] = [];
  @tracked ticketFields: TicketField[] = [];
  @tracked organizationFields: OrganizationField[] = [];

  constructor(inputs: {
    userFields: UserField[];
    ticketFields: TicketField[];
    organizationFields: OrganizationField[];
  }) {
    this.userFields = this.builtInUserFields.concat(inputs.userFields);
    this.ticketFields = this.builtInTicketFields.concat(inputs.ticketFields);
    this.organizationFields = this.builtInOrganiationFields.concat(inputs.organizationFields);
  }

  private get builtInUserFields(): UserField[] {
    return BUILT_IN_ATTRIBUTES.zendeskUserFields.map((field) => {
      return new UserField({
        id: field.platformIdentifier,
        name: this.titleCase(field.platformIdentifier),
        type: 'string',
        required: true,
        intercom_sda: {
          identifier: field.intercomAttribute,
          type: field.intercomFieldType,
        },
        built_in: true,
      });
    });
  }

  get builtInOrganiationFields(): OrganizationField[] {
    return BUILT_IN_ATTRIBUTES.organizationFields.map((field) => {
      return new OrganizationField({
        id: field.platformIdentifier,
        name: this.titleCase(field.platformIdentifier),
        type: 'text',
        required: true,
        intercom_sda: {
          identifier: field.intercomAttribute,
          type: field.intercomFieldType,
        },
        built_in: true,
      });
    });
  }

  get builtInTicketFields(): TicketField[] {
    let fields = BUILT_IN_ATTRIBUTES.ticketFields.map((field) => {
      return new TicketField({
        id: field,
        name: this.titleCase(field),
        type: 'string' as FieldType,
        required: true,
        built_in: true,
      });
    });

    fields.push(
      new TicketField({
        id: 'tags',
        name: this.titleCase('tags'),
        type: 'tagger' as FieldType,
        required: true,
        built_in: true,
      }),
    );

    return fields;
  }

  titleCase(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
